// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { get } from 'lodash/fp';

// Utils
import { stopDefaultAndPropagation, stopPropagationOnly } from '../../../utils/domUtil';

// Actions
import { intercomShowMessages } from '../../../analytics/analyticsActions';
import { refreshPageAndClearLocalCache } from '../../../reducers/navigationActions';

// Components
import LinkButton from '../../buttons/LinkButton';

// Styles
import './ErrorRenderer.scss';

const getDefaultErrorDetails = ({ element, error }) => `Error: ${error && error.toString()}
Stack: ${get('stack', error)}`;

const mapDispatchToProps = (dispatch) => ({
    dispatchToggleIntercom: () => dispatch(intercomShowMessages()),
    dispatchRefreshPageAndClearLocalCache: () => dispatch(refreshPageAndClearLocalCache()),
});

const DefaultErrorRenderer = (props) => {
    const {
        className,
        heading = 'Something went wrong',
        getErrorDetails = getDefaultErrorDetails,
        dispatchToggleIntercom,
        dispatchRefreshPageAndClearLocalCache,
        furtherMessage = '',
        furtherDetails = '',
    } = props;

    const textAreaRef = React.useRef();

    const errorDetails = getErrorDetails(props);

    const copyTextAreaToClipboard = () => {
        if (!textAreaRef.current) return;
        textAreaRef.current.select();
        document.execCommand('copy');
    };

    return (
        <div
            className={classNames('ErrorRenderer', className)}
            onClick={stopPropagationOnly}
            onDoubleClick={stopDefaultAndPropagation}
        >
            <div className="error-message">
                <h3>{heading}</h3>

                <p className="refresh-message">
                    First try{' '}
                    <LinkButton onClick={dispatchRefreshPageAndClearLocalCache}>refreshing the page</LinkButton>
                    {furtherMessage}
                </p>
            </div>
            <details>
                <summary>Otherwise</summary>
                <div className="info">
                    <p className="support-message">
                        Please&nbsp;
                        <LinkButton onClick={dispatchToggleIntercom}>contact support</LinkButton>
                        &nbsp;and provide them with the following information:
                    </p>
                    {furtherDetails}
                    <button className="clipboard" onClick={copyTextAreaToClipboard}>
                        Copy to clipboard
                    </button>
                    <div>
                        <textarea
                            ref={textAreaRef}
                            readOnly
                            value={errorDetails}
                            onFocus={copyTextAreaToClipboard}
                            onClick={copyTextAreaToClipboard}
                        />
                    </div>
                </div>
            </details>
        </div>
    );
};

DefaultErrorRenderer.propTypes = {
    className: PropTypes.string,
    furtherDetails: PropTypes.string,
    furtherMessage: PropTypes.string,
    heading: PropTypes.string,
    dispatchToggleIntercom: PropTypes.func,
    dispatchRefreshPageAndClearLocalCache: PropTypes.func,
    getErrorDetails: PropTypes.func,
    error: PropTypes.object,
};

export default connect(null, mapDispatchToProps)(DefaultErrorRenderer);
